/**
 * Katie & Laura — Notes & photos page
 *
 * Journal list → open/edit/save → back. Soft delete confirm.
 * Scrapbook thumbs open a soft-pink full-size dialog; prev/next under the photo + swipe among visible photos.
 * SOT: Customer metafield diary.notes_photos
 *   { notes: [{id, text, title?, updatedAt}], photos: [{id, url, caption?, source, addedAt}], hiddenStarterIds?: [] }
 *   source: starter | user (shop Save to Diary) | upload (Add my photo)
 * App Proxy:
 *   GET/POST {proxy}/notes-photos — JSON notepad / hide / delete
 *   POST {proxy}/notes-photos { action: 'reorder_photos', photos: [id…] }
 *     photos[] = store saves + uploads in display order (array = SOT)
 *   POST {proxy}/notes-photos/upload — multipart field `file`, source=upload
 *   GET  {proxy}/notes-photos/photos/{id} — authenticated image bytes
 * Personal uploads never go on Wish list share. Soft-fail 404.
 * Starters always come from theme assets so the page is never a blank slate.
 * Optimistic shop-photo saves live in sessionStorage + localStorage
 * (`kl_notes_photos:{customerId}`) and are merged into Liquid/GET docs so an
 * empty 200 from the proxy cannot wipe a photo the shopper just saved.
 * My notes open/closed for the visit: sessionStorage `kl_notes_my_notes_open`.
 */
(function (global) {
  'use strict';

  var DEFAULT_PROXY = '/apps/kl-wishlist';
  var NOTE_MAX = 8000;
  var TITLE_MAX = 120;
  var PREVIEW_MAX = 140;
  var PHOTO_CAP = 100;
  var FILE_MAX = 8 * 1024 * 1024;
  // du1021: phone photos are shrunk on the phone before upload (server limit stays 8 MB).
  var SHRINK_OVER = 5 * 1024 * 1024;
  var SHRINK_STEPS = [[2560, 0.85], [2048, 0.8], [1600, 0.72], [1280, 0.66]];
  var DECODE_TIMEOUT_MS = 20000;
  var STATUS_OK_MS = 3500;
  var MIN_DRAWN_BYTES = 2048; // a real photo jpg is never this small; smaller = blank/failed draw
  var NOTES_OPEN_KEY = 'kl_notes_my_notes_open';

  function readNotesOpen() {
    try {
      var raw = global.sessionStorage && global.sessionStorage.getItem(NOTES_OPEN_KEY);
      if (raw === '0' || raw === 'false') return false;
    } catch (e) {}
    return true;
  }

  function writeNotesOpen(open) {
    try {
      if (global.sessionStorage) global.sessionStorage.setItem(NOTES_OPEN_KEY, open ? '1' : '0');
    } catch (e) {}
  }

  function configFrom(root) {
    return {
      loggedIn: !!(root && root.getAttribute('data-logged-in') === 'true'),
      proxy: proxyPath(root),
      customerId: root ? String(root.getAttribute('data-customer-id') || '') : '',
      saved: (root && root.getAttribute('data-saved')) || 'Saved',
      error: (root && root.getAttribute('data-error')) || 'Couldn’t save just now — try again in a moment.',
      hide: (root && root.getAttribute('data-hide')) || 'Hide',
      keep: (root && root.getAttribute('data-keep')) || 'Keep',
      remove: (root && root.getAttribute('data-remove')) || 'Remove',
      showHidden: (root && root.getAttribute('data-show-hidden')) || 'Show hidden photos',
      noteEmpty: (root && root.getAttribute('data-note-empty')) || 'Nothing written yet — this journal is just for you.',
      untitled: (root && root.getAttribute('data-untitled')) || 'Untitled',
      edit: (root && root.getAttribute('data-edit')) || 'Edit',
      newNote: (root && root.getAttribute('data-new-note')) || 'New note',
      backNotes: (root && root.getAttribute('data-back-notes')) || 'Notes',
      deleteConfirm: (root && root.getAttribute('data-delete-confirm')) || 'Delete this note?',
      deleteYes: (root && root.getAttribute('data-delete-yes')) || 'Delete',
      photoRemoveConfirm: (root && root.getAttribute('data-photo-remove-confirm')) || 'Remove this photo?',
      cancel: (root && root.getAttribute('data-cancel')) || 'Cancel',
      openPhoto: (root && root.getAttribute('data-open-photo')) || 'View full photo',
      closePhoto: (root && root.getAttribute('data-close-photo')) || 'Close photo',
      addPhoto: (root && root.getAttribute('data-add-photo')) || 'Add my photo',
      photoAdded: (root && root.getAttribute('data-photo-added')) || 'Photo added',
      photoRemoved: (root && root.getAttribute('data-photo-removed')) || 'Photo removed',
      photoAdding: (root && root.getAttribute('data-photo-adding')) || 'Adding photo…',
      photoCap: (root && root.getAttribute('data-photo-cap')) || 'You can keep up to 100 of your photos.',
      photoTooLarge: (root && root.getAttribute('data-photo-too-large')) || 'That photo is too large.',
      photoType: (root && root.getAttribute('data-photo-type')) || 'Choose a photo.',
      reorderHint: (root && root.getAttribute('data-reorder-hint')) || 'Drag to rearrange',
      reorderError: (root && root.getAttribute('data-reorder-error')) || 'Couldn’t save the new order — try again in a moment.',
      // du1021 photo-upload help (plain words: what went wrong + what to do)
      photoPreparing: (root && root.getAttribute('data-photo-preparing')) || 'Getting your photo ready…',
      photoTypeHelp: (root && root.getAttribute('data-photo-type-help')) || 'That file isn’t a photo we can add. Please pick a jpg, png, or webp photo from your photo library.',
      photoTooBig: (root && root.getAttribute('data-photo-too-big')) || 'That photo is too big to add, even after we tried to shrink it. Please try a different photo, or take a screenshot of it and add the screenshot.',
      photoHeic: (root && root.getAttribute('data-photo-heic')) || 'This photo is in Apple’s HEIC format, which this browser can’t open. Please pick it from your Photos library instead of Files, or take a screenshot of it and add the screenshot.',
      photoSignedOut: (root && root.getAttribute('data-photo-signed-out')) || 'You’ve been signed out, so your photo didn’t save. Please sign in again, then tap Add my photo.',
      photoNetwork: (root && root.getAttribute('data-photo-network')) || 'Your photo didn’t upload — the connection dropped. Check your signal or Wi-Fi, then tap Add my photo again.',
      photoRejected: (root && root.getAttribute('data-photo-rejected')) || 'That photo couldn’t be added. It needs to be a jpg, png, or webp under 8 MB. Please try a different photo, or a screenshot of it.',
      photoUploadError: (root && root.getAttribute('data-photo-upload-error')) || 'Your photo didn’t save just now. Please tap Add my photo and try again. If it keeps happening, email Katie with a screenshot of this message.',
      photoDraw: (root && root.getAttribute('data-photo-draw')) || 'Your phone couldn’t get this photo ready to upload. Please tap Add my photo and try again, or take a screenshot of the photo and add the screenshot.',
      dismiss: (root && root.getAttribute('data-dismiss')) || 'OK'
    };
  }

  function proxyPath(root) {
    var raw = String((root && root.getAttribute('data-proxy')) || DEFAULT_PROXY).trim();
    if (!raw) raw = DEFAULT_PROXY;
    if (raw.charAt(0) !== '/') raw = '/' + raw;
    return raw.replace(/\/+$/, '');
  }

  function parseJsonScript(root, selector) {
    var el = root && root.querySelector(selector);
    if (!el) return null;
    var text = el.textContent || el.innerText || '';
    if (!text || !String(text).replace(/^\s+|\s+$/g, '')) return null;
    try {
      return JSON.parse(text);
    } catch (e) {
      return null;
    }
  }

  function uid(prefix) {
    return prefix + '-' + Date.now().toString(36) + Math.random().toString(36).slice(2, 8);
  }

  function trim(value, max) {
    var out = String(value == null ? '' : value).replace(/^\s+|\s+$/g, '');
    if (max && out.length > max) out = out.slice(0, max);
    return out;
  }

  function normalizeNote(raw) {
    if (!raw || typeof raw !== 'object') return null;
    var text = trim(raw.text, NOTE_MAX);
    var title = trim(raw.title, TITLE_MAX);
    if (!text && !title) return null;
    return {
      id: String(raw.id || uid('note')),
      text: text,
      title: title,
      updatedAt: raw.updatedAt ? String(raw.updatedAt) : new Date().toISOString()
    };
  }

  function photoSource(raw) {
    if (!raw) return 'user';
    if (raw.source === 'starter') return 'starter';
    if (raw.source === 'upload') return 'upload';
    return 'user';
  }

  function authImagePath(proxy, id) {
    var base = proxy || DEFAULT_PROXY;
    return base + '/notes-photos/photos/' + encodeURIComponent(id);
  }

  function rewriteAuthUrl(url, proxy, id) {
    var raw = String(url || '').trim();
    if (!raw) return '';
    if (/^blob:/i.test(raw) || /^data:/i.test(raw)) return raw;
    if (/\/notes-photos\/image\//i.test(raw) && id) return authImagePath(proxy, id);
    if (/workers\.dev\//i.test(raw) && id) return authImagePath(proxy, id);
    return raw;
  }

  function normalizePhoto(raw, proxy) {
    if (!raw || typeof raw !== 'object') return null;
    var source = photoSource(raw);
    var id = String(raw.id || uid('photo'));
    var url = String(raw.url || raw.src || raw.image || raw.authUrl || raw.imagePath || '').trim();
    url = rewriteAuthUrl(url, proxy, id);
    if (!url && source === 'upload' && id) url = authImagePath(proxy, id);
    if (!url) return null;
    return {
      id: id,
      url: url,
      caption: raw.caption ? String(raw.caption) : '',
      source: source,
      addedAt: raw.addedAt ? String(raw.addedAt) : new Date().toISOString()
    };
  }

  function normalizeDoc(value, starters, proxy) {
    var raw = value;
    if (typeof raw === 'string') {
      try {
        raw = JSON.parse(raw);
      } catch (e) {
        raw = null;
      }
    }
    if (!raw || typeof raw !== 'object') raw = {};
    var notes = [];
    var list = Array.isArray(raw.notes) ? raw.notes : [];
    var seenNotes = {};
    for (var i = 0; i < list.length; i++) {
      var note = normalizeNote(list[i]);
      if (!note || seenNotes[note.id]) continue;
      if (note.productId || note.product_id) continue;
      seenNotes[note.id] = true;
      notes.push(note);
    }
    var hidden = [];
    var hiddenRaw = Array.isArray(raw.hiddenStarterIds) ? raw.hiddenStarterIds : [];
    for (var h = 0; h < hiddenRaw.length; h++) {
      var hid = String(hiddenRaw[h] || '');
      if (hid && hidden.indexOf(hid) < 0) hidden.push(hid);
    }
    var photos = [];
    var seenPhotos = {};
    var starterList = Array.isArray(starters) ? starters : [];
    for (var s = 0; s < starterList.length; s++) {
      var starter = normalizePhoto(starterList[s], proxy || DEFAULT_PROXY);
      if (!starter) continue;
      starter.source = 'starter';
      seenPhotos[starter.id] = true;
      photos.push(starter);
    }
    var incoming = Array.isArray(raw.photos) ? raw.photos : [];
    for (var p = 0; p < incoming.length; p++) {
      var photo = normalizePhoto(incoming[p], proxy || DEFAULT_PROXY);
      if (!photo || seenPhotos[photo.id]) continue;
      if (photo.source === 'starter') continue;
      seenPhotos[photo.id] = true;
      photos.push(photo);
    }
    return { notes: notes, photos: photos, hiddenStarterIds: hidden };
  }

  function mergeDocs(base, extra) {
    var out = {
      notes: base && Array.isArray(base.notes) ? base.notes.slice() : [],
      photos: base && Array.isArray(base.photos) ? base.photos.slice() : [],
      hiddenStarterIds: base && Array.isArray(base.hiddenStarterIds) ? base.hiddenStarterIds.slice() : []
    };
    if (!extra || typeof extra !== 'object') return out;
    var seenNotes = {};
    for (var i = 0; i < out.notes.length; i++) seenNotes[out.notes[i].id] = true;
    var extraNotes = Array.isArray(extra.notes) ? extra.notes : [];
    for (var n = 0; n < extraNotes.length; n++) {
      var note = extraNotes[n];
      if (!note || seenNotes[note.id]) continue;
      seenNotes[note.id] = true;
      out.notes.push(note);
    }
    var seenPhotoIds = {};
    var seenPhotoUrls = {};
    for (var p = 0; p < out.photos.length; p++) {
      seenPhotoIds[out.photos[p].id] = true;
      if (out.photos[p].url) seenPhotoUrls[out.photos[p].url] = true;
    }
    var extraPhotos = Array.isArray(extra.photos) ? extra.photos : [];
    for (var q = 0; q < extraPhotos.length; q++) {
      var photo = extraPhotos[q];
      if (!photo || photo.source === 'starter') continue;
      if (seenPhotoIds[photo.id] || (photo.url && seenPhotoUrls[photo.url])) continue;
      seenPhotoIds[photo.id] = true;
      if (photo.url) seenPhotoUrls[photo.url] = true;
      out.photos.push(photo);
    }
    var extraHidden = Array.isArray(extra.hiddenStarterIds) ? extra.hiddenStarterIds : [];
    for (var h = 0; h < extraHidden.length; h++) {
      var hid = extraHidden[h];
      if (hid && out.hiddenStarterIds.indexOf(hid) < 0) out.hiddenStarterIds.push(hid);
    }
    return out;
  }

  function sessionKey(customerId) {
    return 'kl_notes_photos:' + (customerId || 'anon');
  }

  function readStore(store, customerId) {
    if (!store) return null;
    try {
      var raw = store.getItem(sessionKey(customerId));
      if (!raw) return null;
      return JSON.parse(raw);
    } catch (e) {
      return null;
    }
  }

  function readSession(customerId) {
    var session = readStore(global.sessionStorage, customerId);
    var local = readStore(global.localStorage, customerId);
    if (session && local) {
      return {
        notes: [].concat(session.notes || [], local.notes || []),
        photos: [].concat(session.photos || [], local.photos || []),
        hiddenStarterIds: [].concat(session.hiddenStarterIds || [], local.hiddenStarterIds || [])
      };
    }
    return session || local;
  }

  function isPersonalSource(source) {
    return source === 'user' || source === 'upload';
  }

  function personalIdsFrom(doc) {
    var ids = [];
    var list = doc && Array.isArray(doc.photos) ? doc.photos : [];
    for (var i = 0; i < list.length; i++) {
      if (list[i] && isPersonalSource(list[i].source) && list[i].id) ids.push(list[i].id);
    }
    return ids;
  }

  function sameIdList(a, b) {
    if (!a || !b || a.length !== b.length) return false;
    for (var i = 0; i < a.length; i++) {
      if (a[i] !== b[i]) return false;
    }
    return true;
  }

  function applyPersonalOrder(doc, ids) {
    var personal = {};
    var starters = [];
    var list = doc && Array.isArray(doc.photos) ? doc.photos : [];
    for (var i = 0; i < list.length; i++) {
      var photo = list[i];
      if (!photo) continue;
      if (isPersonalSource(photo.source)) personal[photo.id] = photo;
      else starters.push(photo);
    }
    var ordered = [];
    var seen = {};
    var want = Array.isArray(ids) ? ids : [];
    for (var j = 0; j < want.length; j++) {
      var id = want[j];
      if (id && personal[id] && !seen[id]) {
        ordered.push(personal[id]);
        seen[id] = true;
      }
    }
    for (var key in personal) {
      if (Object.prototype.hasOwnProperty.call(personal, key) && !seen[key]) {
        ordered.push(personal[key]);
      }
    }
    if (!doc) doc = { notes: [], photos: [], hiddenStarterIds: [] };
    doc.photos = starters.concat(ordered);
    return doc;
  }

  function persistableDoc(doc) {
    var photos = [];
    var list = doc && Array.isArray(doc.photos) ? doc.photos : [];
    for (var i = 0; i < list.length; i++) {
      var photo = list[i];
      if (!photo || !photo.url) continue;
      if (/^blob:/i.test(photo.url)) continue;
      photos.push(photo);
    }
    return {
      notes: doc && Array.isArray(doc.notes) ? doc.notes : [],
      photos: photos,
      hiddenStarterIds: doc && Array.isArray(doc.hiddenStarterIds) ? doc.hiddenStarterIds : []
    };
  }

  function writeSession(customerId, doc) {
    var raw = JSON.stringify(persistableDoc(doc));
    try {
      global.sessionStorage.setItem(sessionKey(customerId), raw);
    } catch (e) {}
    try {
      global.localStorage.setItem(sessionKey(customerId), raw);
    } catch (e) {}
  }

  function proxyGet(proxy) {
    return fetch(proxy + '/notes-photos', {
      method: 'GET',
      credentials: 'same-origin',
      headers: { Accept: 'application/json' }
    }).then(function (res) {
      if (!res.ok) {
        var err = new Error('kl-notes-photos ' + res.status);
        err.status = res.status;
        throw err;
      }
      return res.json().catch(function () {
        return {};
      });
    });
  }

  function proxyPost(proxy, body) {
    return fetch(proxy + '/notes-photos', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    }).then(function (res) {
      if (!res.ok) {
        var err = new Error('kl-notes-photos ' + res.status);
        err.status = res.status;
        throw err;
      }
      return res.json().catch(function () {
        return {};
      });
    });
  }

  function proxyUpload(proxy, file, extra) {
    var form = new FormData();
    form.append('file', file, file.name || 'photo.jpg');
    form.append('source', 'upload');
    if (extra && extra.caption) form.append('caption', extra.caption);
    if (extra && extra.addedAt) form.append('addedAt', extra.addedAt);
    return fetch(proxy + '/notes-photos/upload', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
      body: form
    }).then(function (res) {
      return res.text().then(function (text) {
        var data = {};
        if (text) {
          try {
            data = JSON.parse(text);
          } catch (e) {
            data = {};
          }
        }
        if (!res.ok) {
          var message = data && data.error ? String(data.error) : 'kl-notes-photos-upload ' + res.status;
          var err = new Error(message);
          err.status = res.status;
          err.body = data;
          err.error = data && data.error ? String(data.error) : '';
          throw err;
        }
        return data;
      });
    });
  }

  function photoFromResponse(data, proxy) {
    if (!data || typeof data !== 'object') return null;
    if (data.photo) return normalizePhoto(data.photo, proxy);
    if (data.item && data.item.url) return normalizePhoto(data.item, proxy);
    if (data.id && (data.url || data.source === 'upload')) return normalizePhoto(data, proxy);
    return null;
  }

  function docFromResponse(data, starters, proxy) {
    if (!data || typeof data !== 'object') return null;
    if (data.notes || data.photos || data.hiddenStarterIds) return normalizeDoc(data, starters, proxy);
    if (data.item || data.document || data.value) {
      return normalizeDoc(data.item || data.document || data.value, starters, proxy);
    }
    return null;
  }

  function escapeHtml(str) {
    return String(str == null ? '' : str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function formatWhen(iso) {
    if (!iso) return '';
    try {
      var d = new Date(iso);
      if (isNaN(d.getTime())) return '';
      return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
    } catch (e) {
      return '';
    }
  }

  function previewText(text) {
    var t = String(text || '').replace(/\s+/g, ' ').replace(/^\s+|\s+$/g, '');
    if (t.length > PREVIEW_MAX) t = t.slice(0, PREVIEW_MAX - 1) + '…';
    return t;
  }

  function dialogOpen(el) {
    if (!el) return;
    if (typeof el.showModal === 'function') {
      if (!el.open) el.showModal();
      return;
    }
    el.setAttribute('open', '');
    el.removeAttribute('hidden');
  }

  function dialogClose(el) {
    if (!el) return;
    if (typeof el.close === 'function') {
      if (el.open) el.close();
      return;
    }
    el.removeAttribute('open');
    el.setAttribute('hidden', '');
  }

  // ---- du1021: phone photo prep (HEIC / huge camera photos) -------------
  function fileKind(file) {
    if (!file) return 'none';
    var type = String(file.type || '').toLowerCase();
    var name = String(file.name || '').toLowerCase();
    if (/^image\/(heic|heif)(-sequence)?$/.test(type) || /\.(heic|heif)$/.test(name)) return 'heic';
    if (type === 'image/jpeg' || type === 'image/jpg' || type === 'image/png' || type === 'image/webp') return 'direct';
    if (!type && /\.(jpe?g|png|webp)$/.test(name)) return 'direct';
    if (/^image\//.test(type)) return 'convert';
    if (!type) return 'convert'; // some phone pickers send no type; try to open it
    return 'reject';
  }

  function prepError(code) {
    var err = new Error('kl-notes-photos-prep ' + code);
    err.prep = code;
    return err;
  }

  function loadImage(file, env) {
    env = env || global;
    return new Promise(function (resolve, reject) {
      var URLs = env.URL || global.URL;
      var url = '';
      try {
        url = URLs.createObjectURL(file);
      } catch (e) {
        reject(prepError('decode'));
        return;
      }
      var done = false;
      var img = new (env.Image || global.Image)();
      var timer = setTimeout(function () {
        finish(null);
      }, DECODE_TIMEOUT_MS);
      function finish(ok) {
        if (done) return;
        done = true;
        clearTimeout(timer);
        try {
          URLs.revokeObjectURL(url);
        } catch (e) {}
        if (ok && (img.naturalWidth || img.width)) resolve(img);
        else reject(prepError('decode'));
      }
      img.onload = function () {
        finish(true);
      };
      img.onerror = function () {
        finish(false);
      };
      img.src = url;
    });
  }

  function canvasToJpeg(img, maxEdge, quality, env) {
    env = env || global;
    var w = img.naturalWidth || img.width;
    var h = img.naturalHeight || img.height;
    var scale = Math.min(1, maxEdge / Math.max(w, h));
    var cw = Math.max(1, Math.round(w * scale));
    var ch = Math.max(1, Math.round(h * scale));
    var doc = env.document || global.document;
    var canvas = doc.createElement('canvas');
    canvas.width = cw;
    canvas.height = ch;
    var ctx = canvas.getContext && canvas.getContext('2d');
    if (!ctx) return Promise.reject(prepError('draw'));
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, cw, ch);
    ctx.drawImage(img, 0, 0, cw, ch);
    return new Promise(function (resolve, reject) {
      if (typeof canvas.toBlob !== 'function') {
        reject(prepError('draw'));
        return;
      }
      canvas.toBlob(function (blob) {
        canvas.width = 0;
        canvas.height = 0;
        if (blob && blob.size >= MIN_DRAWN_BYTES) resolve(blob);
        else reject(prepError('draw'));
      }, 'image/jpeg', quality);
    });
  }

  function jpegName(name) {
    var base = String(name || 'photo').replace(/\.[^.\/]*$/, '') || 'photo';
    return base.slice(0, 80) + '.jpg';
  }

  function asJpegFile(blob, name, env) {
    env = env || global;
    var F = env.File || global.File;
    try {
      if (typeof F === 'function') return new F([blob], jpegName(name), { type: 'image/jpeg' });
    } catch (e) {}
    try {
      blob.name = jpegName(name);
    } catch (e) {}
    return blob;
  }

  // Resolves to the File/Blob to upload; rejects with err.prep in
  // 'type' | 'heic' | 'too_large' | 'decode' | 'draw'.
  function prepareUploadFile(file, env) {
    var kind = fileKind(file);
    if (kind === 'none' || kind === 'reject') return Promise.reject(prepError('type'));
    if (kind === 'direct' && (!file.size || file.size <= SHRINK_OVER)) return Promise.resolve(file);
    return loadImage(file, env).then(
      function (img) {
        var i = 0;
        function next() {
          if (i >= SHRINK_STEPS.length) return Promise.reject(prepError('too_large'));
          var step = SHRINK_STEPS[i++];
          return canvasToJpeg(img, step[0], step[1], env).then(function (blob) {
            if (blob.size <= FILE_MAX && blob.size <= SHRINK_OVER) return asJpegFile(blob, file.name, env);
            if (i >= SHRINK_STEPS.length && blob.size <= FILE_MAX) return asJpegFile(blob, file.name, env);
            return next();
          });
        }
        return next();
      },
      function () {
        if (kind === 'heic') throw prepError('heic');
        if (kind === 'direct') {
          if (file.size > FILE_MAX) throw prepError('too_large');
          return file; // could not shrink but already under the limit: send as-is
        }
        throw prepError('type');
      }
    );
  }

  // Plain-words message for a failed upload (server/network side).
  function uploadErrorMessage(err, cfg) {
    var status = err && err.status;
    var text = err && err.error ? String(err.error) : '';
    if (err && err.prep) {
      if (err.prep === 'heic') return cfg.photoHeic;
      if (err.prep === 'too_large') return cfg.photoTooBig;
      if (err.prep === 'draw') return cfg.photoDraw;
      return cfg.photoTypeHelp;
    }
    if (!status) return cfg.photoNetwork;
    if (status === 401 || status === 403) return cfg.photoSignedOut;
    if (status === 409 || (status === 400 && /limited to/i.test(text))) return cfg.photoCap;
    if (status === 413) return cfg.photoTooBig;
    if (status === 400 && /8\s*MB|jpg|png|webp/i.test(text)) return cfg.photoRejected;
    return cfg.photoUploadError;
  }
  // ---- end du1021 helpers --------------------------------------------------

  function bindRoot(root) {
    if (!root || root.getAttribute('data-kl-bound') === '1') return;
    root.setAttribute('data-kl-bound', '1');

    var cfg = configFrom(root);
    var starters = parseJsonScript(root, '[data-kl-notes-starters-json]') || [];
    var liquidDoc = normalizeDoc(parseJsonScript(root, '[data-kl-notes-doc-json]'), starters, cfg.proxy);
    var sessionDoc = cfg.loggedIn ? normalizeDoc(readSession(cfg.customerId), starters, cfg.proxy) : null;
    var doc = mergeDocs(liquidDoc, sessionDoc);

    var listEl = root.querySelector('[data-kl-notes-list]');
    var emptyEl = root.querySelector('[data-kl-notes-empty]');
    var photosEl = root.querySelector('[data-kl-notes-photos]');
    var hiddenBtn = root.querySelector('[data-kl-notes-hidden-toggle]');
    var statusEl = root.querySelector('[data-kl-notes-status]');
    var listStatusEl = root.querySelector('[data-kl-notes-list-status]');
    var photoStatusEl = root.querySelector('[data-kl-notes-photo-status]');
    var addBtn = root.querySelector('[data-kl-notes-add]');
    var uploadInput = root.querySelector('[data-kl-notes-upload]');
    var titleInput = root.querySelector('[data-kl-notes-title]');
    var bodyInput = root.querySelector('[data-kl-notes-body]');
    var editorDelete = root.querySelector('[data-kl-notes-editor-delete]');
    var lightbox = root.querySelector('[data-kl-notes-lightbox]');
    var lightboxImg = root.querySelector('[data-kl-lightbox-img]');
    var lightboxCap = root.querySelector('[data-kl-lightbox-cap]');
    var lightboxPrev = root.querySelector('[data-kl-lightbox-prev]');
    var lightboxNext = root.querySelector('[data-kl-lightbox-next]');
    var lightboxNavs = root.querySelector('[data-kl-lightbox-navs]');
    var confirmEl = root.querySelector('[data-kl-notes-confirm]');
    var confirmTitle = confirmEl && confirmEl.querySelector('#kl-notes-confirm-title');
    var confirmOkBtn = confirmEl && confirmEl.querySelector('[data-kl-notes-confirm-ok]');
    var showHidden = false;
    var pendingDeleteId = '';
    var pendingConfirmFn = null;
    var lastListFocus = null;
    var lightboxIndex = 0;
    var swipeIgnoreClick = false;
    var skipPhotoOpen = false;
    var dragState = null;
    var uploadBusy = false;

    function persist() {
      if (cfg.loggedIn) writeSession(cfg.customerId, doc);
    }

    function applyNotesCollapse(open) {
      var section = root.querySelector('[data-kl-notes-collapse="notes"]');
      var toggle = root.querySelector('[data-kl-notes-toggle]');
      var body = root.querySelector('[data-kl-notes-collapse-body]');
      if (section) section.classList.toggle('is-collapsed', !open);
      if (toggle) toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
      if (body) {
        if (open) body.removeAttribute('hidden');
        else body.setAttribute('hidden', '');
      }
    }

    // du1021: errors stay on screen until dismissed (OK) or replaced; they are
    // large and readable on phones. Progress (sticky) stays until replaced.
    // Plain success notes stay 3.5s (was 2.2s).
    function flashStatus(el, message, isError, sticky) {
      if (!el) return;
      clearTimeout(el._klTimer);
      el.hidden = !message;
      el.textContent = message || '';
      el.classList.toggle('is-error', !!(message && isError));
      el.setAttribute('aria-live', message && isError ? 'assertive' : 'polite');
      if (message && isError) {
        el.setAttribute('role', 'alert');
        var ok = el.ownerDocument.createElement('button');
        ok.type = 'button';
        ok.className = 'kl-notes-status__ok';
        ok.textContent = cfg.dismiss;
        ok.addEventListener('click', function () {
          el.hidden = true;
          el.textContent = '';
          el.classList.remove('is-error');
        });
        el.appendChild(ok);
        return;
      }
      el.setAttribute('role', 'status');
      if (message && !sticky) {
        el._klTimer = setTimeout(function () {
          el.hidden = true;
        }, STATUS_OK_MS);
      }
    }

    function showStatus(message, isError, sticky) {
      flashStatus(statusEl, message, isError, sticky);
      flashStatus(listStatusEl, message, isError, sticky);
      flashStatus(photoStatusEl, message, isError, sticky);
      if (message && isError && photoStatusEl && !photoStatusEl.hidden && typeof photoStatusEl.scrollIntoView === 'function') {
        try {
          photoStatusEl.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        } catch (e) {}
      }
    }

    function uploadCount() {
      var n = 0;
      for (var i = 0; i < doc.photos.length; i++) {
        if (doc.photos[i] && doc.photos[i].source === 'upload') n += 1;
      }
      return n;
    }

    function setAdding(on) {
      if (addBtn) addBtn.disabled = !!on;
      if (uploadInput) uploadInput.disabled = !!on;
    }

    function postAction(body, successMsg) {
      if (!cfg.loggedIn) return Promise.resolve();
      persist();
      return proxyPost(cfg.proxy, body)
        .then(function (data) {
          var incoming = docFromResponse(data, starters, cfg.proxy);
          if (incoming) {
            doc = mergeDocs(incoming, doc);
            persist();
            paint();
          }
          showStatus(successMsg || cfg.saved, false);
        })
        .catch(function () {
          showStatus(successMsg || cfg.saved, false);
        });
    }

    function isEditing() {
      return root.classList.contains('is-editing');
    }

    function openEditor(note) {
      if (!cfg.loggedIn) return;
      root.classList.add('is-editing');
      if (note) {
        root.setAttribute('data-editing-id', note.id);
        if (titleInput) titleInput.value = note.title || '';
        if (bodyInput) bodyInput.value = note.text || '';
        if (editorDelete) editorDelete.hidden = false;
      } else {
        root.removeAttribute('data-editing-id');
        if (titleInput) titleInput.value = '';
        if (bodyInput) bodyInput.value = '';
        if (editorDelete) editorDelete.hidden = true;
      }
      showStatus('', false);
      var focusEl = titleInput || bodyInput;
      if (focusEl && typeof focusEl.focus === 'function') {
        try {
          focusEl.focus();
        } catch (e) {}
      }
    }

    function closeEditor(restoreFocus) {
      root.classList.remove('is-editing');
      root.removeAttribute('data-editing-id');
      if (titleInput) titleInput.value = '';
      if (bodyInput) bodyInput.value = '';
      if (editorDelete) editorDelete.hidden = true;
      flashStatus(statusEl, '', false);
      if (restoreFocus && lastListFocus && typeof lastListFocus.focus === 'function' && document.contains(lastListFocus)) {
        try {
          lastListFocus.focus();
        } catch (e) {}
      }
    }

    function paintNotes() {
      if (!listEl) return;
      if (!doc.notes.length) {
        listEl.innerHTML = '';
        if (emptyEl) emptyEl.hidden = false;
        return;
      }
      if (emptyEl) emptyEl.hidden = true;
      var html = '';
      for (var i = 0; i < doc.notes.length; i++) {
        var note = doc.notes[i];
        var heading = note.title || cfg.untitled;
        var preview = previewText(note.text);
        html +=
          '<article class="kl-notes-card" data-kl-note-id="' +
          escapeHtml(note.id) +
          '">' +
          '<button type="button" class="kl-notes-card__open" data-kl-note-open>' +
          '<header class="kl-notes-card__top">' +
          '<h3 class="kl-notes-card__title">' +
          escapeHtml(heading) +
          '</h3>' +
          (note.updatedAt
            ? '<time class="kl-notes-card__when" datetime="' +
              escapeHtml(note.updatedAt) +
              '">' +
              escapeHtml(formatWhen(note.updatedAt)) +
              '</time>'
            : '') +
          '</header>' +
          (preview ? '<p class="kl-notes-card__body">' + escapeHtml(preview) + '</p>' : '') +
          '</button>' +
          (cfg.loggedIn
            ? '<div class="kl-notes-card__actions">' +
              '<button type="button" class="kl-notes-quiet" data-kl-note-delete>' +
              escapeHtml(cfg.remove) +
              '</button>' +
              '</div>'
            : '') +
          '</article>';
      }
      listEl.innerHTML = html;
    }

    function personalPhotoHtml(photo) {
      return (
        '<figure class="kl-notes-photo kl-notes-photo--personal" data-kl-photo data-photo-id="' +
        escapeHtml(photo.id) +
        '" data-source="' +
        escapeHtml(photo.source) +
        '">' +
        (cfg.loggedIn
          ? '<button type="button" class="kl-notes-photo__grip" data-kl-photo-grip aria-label="' +
            escapeHtml(cfg.reorderHint) +
            '"><span class="kl-notes-photo__grip-dots" aria-hidden="true"></span></button>'
          : '') +
        '<button type="button" class="kl-notes-photo__open" data-kl-photo-open aria-label="' +
        escapeHtml(cfg.openPhoto) +
        '">' +
        '<img src="' +
        escapeHtml(photo.url) +
        '" alt="' +
        escapeHtml(photo.caption || '') +
        '" loading="lazy" width="800" height="1000" draggable="false">' +
        '</button>' +
        (photo.caption ? '<figcaption>' + escapeHtml(photo.caption) + '</figcaption>' : '') +
        (cfg.loggedIn
          ? '<button type="button" class="kl-notes-quiet" data-kl-remove-photo>' +
            escapeHtml(cfg.remove) +
            '</button>'
          : '') +
        '</figure>'
      );
    }

    function paintPhotos() {
      if (!photosEl) return;
      var figures = photosEl.querySelectorAll('[data-kl-photo]');
      var hiddenCount = 0;
      for (var i = 0; i < figures.length; i++) {
        var fig = figures[i];
        var id = fig.getAttribute('data-photo-id') || '';
        var source = fig.getAttribute('data-source') || '';
        var hidden = source === 'starter' && doc.hiddenStarterIds.indexOf(id) >= 0;
        if (hidden) hiddenCount += 1;
        fig.hidden = !!(hidden && !showHidden);
        fig.classList.toggle('is-hidden-starter', hidden);
        var hideBtn = fig.querySelector('[data-kl-hide-starter]');
        if (hideBtn) {
          hideBtn.textContent = hidden ? cfg.keep : cfg.hide;
          hideBtn.hidden = !cfg.loggedIn;
        }
      }
      var existingPersonal = photosEl.querySelectorAll('[data-kl-photo][data-source="user"], [data-kl-photo][data-source="upload"]');
      for (var u = 0; u < existingPersonal.length; u++) existingPersonal[u].remove();
      var personal = [];
      for (var p = 0; p < doc.photos.length; p++) {
        var photo = doc.photos[p];
        if (photo && isPersonalSource(photo.source)) personal.push(photo);
      }
      var html = '';
      for (var q = 0; q < personal.length; q++) html += personalPhotoHtml(personal[q]);
      if (html) photosEl.insertAdjacentHTML('afterbegin', html);
      photosEl.classList.toggle('is-sortable', !!(cfg.loggedIn && personal.length > 1));
      if (hiddenBtn) {
        hiddenBtn.hidden = !(cfg.loggedIn && hiddenCount);
        hiddenBtn.textContent = cfg.showHidden;
        hiddenBtn.setAttribute('aria-pressed', showHidden ? 'true' : 'false');
      }
    }

    function lightboxPhotoId() {
      if (!lightboxIsOpen()) return '';
      var figs = visiblePhotos();
      var fig = figs[lightboxIndex];
      return fig ? fig.getAttribute('data-photo-id') || '' : '';
    }

    function realignLightbox(photoId) {
      if (!photoId || !lightboxIsOpen()) return;
      var figs = visiblePhotos();
      var idx = 0;
      for (var i = 0; i < figs.length; i++) {
        if (figs[i].getAttribute('data-photo-id') === photoId) {
          idx = i;
          break;
        }
      }
      showLightboxAt(idx, figs);
    }

    function paint() {
      var keepId = lightboxPhotoId();
      var editingId = root.getAttribute('data-editing-id') || '';
      paintNotes();
      paintPhotos();
      if (editorDelete) editorDelete.hidden = !(isEditing() && editingId);
      realignLightbox(keepId);
    }

    function findNote(id) {
      for (var i = 0; i < doc.notes.length; i++) {
        if (doc.notes[i].id === id) return doc.notes[i];
      }
      return null;
    }

    function upsertNote(fromSave) {
      if (!cfg.loggedIn) return;
      var title = titleInput ? trim(titleInput.value, TITLE_MAX) : '';
      var text = bodyInput ? trim(bodyInput.value, NOTE_MAX) : '';
      var editingId = root.getAttribute('data-editing-id') || '';
      if (!title && !text) {
        if (fromSave) showStatus(cfg.saved, false);
        return;
      }
      var next = {
        id: editingId || uid('note'),
        title: title,
        text: text,
        updatedAt: new Date().toISOString()
      };
      var found = false;
      doc.notes = doc.notes.map(function (row) {
        if (row.id === next.id) {
          found = true;
          return next;
        }
        return row;
      });
      if (!found) doc.notes = [next].concat(doc.notes);
      persist();
      paint();
      var upsertBody = { action: 'upsert_note', text: next.text };
      if (next.id) upsertBody.id = next.id;
      if (next.title) upsertBody.title = next.title;
      postAction(upsertBody);
      lastListFocus = null;
      closeEditor(false);
    }

    function editNote(id) {
      var note = findNote(id);
      if (!note) return;
      openEditor(note);
    }

    function deleteNote(id) {
      if (!id) return;
      doc.notes = doc.notes.filter(function (row) {
        return row.id !== id;
      });
      if (root.getAttribute('data-editing-id') === id) closeEditor(false);
      paint();
      postAction({ action: 'delete_note', id: id });
    }

    function setConfirmCopy(message, okLabel) {
      if (confirmTitle) confirmTitle.textContent = message || cfg.deleteConfirm;
      if (confirmOkBtn) confirmOkBtn.textContent = okLabel || cfg.deleteYes;
    }

    function askConfirm(message, okLabel, onYes) {
      var ask = global.klDiaryConfirm;
      if (typeof ask === 'function' && document.querySelector('[data-kl-diary-confirm]')) {
        ask(message, function (ok) {
          if (ok && onYes) onYes();
        }, okLabel);
        return;
      }
      pendingConfirmFn = onYes;
      setConfirmCopy(message, okLabel);
      if (confirmEl) dialogOpen(confirmEl);
      else if (onYes && typeof global.confirm === 'function' && global.confirm(message)) onYes();
    }

    function askDelete(id) {
      if (!id) return;
      pendingDeleteId = id;
      askConfirm(cfg.deleteConfirm, cfg.deleteYes, function () {
        deleteNote(id);
      });
    }

    function confirmDelete() {
      var id = pendingDeleteId;
      var fn = pendingConfirmFn;
      pendingDeleteId = '';
      pendingConfirmFn = null;
      dialogClose(confirmEl);
      if (fn) fn();
      else if (id) deleteNote(id);
    }

    function cancelDelete() {
      pendingDeleteId = '';
      pendingConfirmFn = null;
      dialogClose(confirmEl);
    }

    function toggleStarter(id) {
      if (!id) return;
      var idx = doc.hiddenStarterIds.indexOf(id);
      if (idx >= 0) doc.hiddenStarterIds.splice(idx, 1);
      else doc.hiddenStarterIds.push(id);
      paint();
      if (idx < 0) postAction({ action: 'hide_starter', id: id });
    }

    function removePhoto(id) {
      var doomed = null;
      doc.photos = doc.photos.filter(function (row) {
        if (row.id === id) {
          doomed = row;
          return false;
        }
        return true;
      });
      if (doomed && doomed.url && /^blob:/i.test(doomed.url)) {
        try {
          URL.revokeObjectURL(doomed.url);
        } catch (e) {}
      }
      paint();
      postAction({ action: 'delete_photo', id: id }, cfg.photoRemoved);
    }

    function addOptimisticUpload(file) {
      var preview = '';
      try {
        preview = URL.createObjectURL(file);
      } catch (e) {
        preview = '';
      }
      var photo = {
        id: uid('upload'),
        url: preview || authImagePath(cfg.proxy, 'pending'),
        caption: '',
        source: 'upload',
        addedAt: new Date().toISOString()
      };
      doc.photos = [photo].concat(doc.photos);
      persist();
      paint();
      return photo;
    }

    function replacePhoto(oldId, next) {
      var found = false;
      doc.photos = doc.photos.map(function (row) {
        if (row.id !== oldId) return row;
        found = true;
        if (row.url && /^blob:/i.test(row.url) && next.url !== row.url) {
          try {
            URL.revokeObjectURL(row.url);
          } catch (e) {}
        }
        return next;
      });
      if (!found && next) doc.photos = [next].concat(doc.photos);
      persist();
      paint();
    }

    function dropPhoto(id) {
      var doomed = null;
      doc.photos = doc.photos.filter(function (row) {
        if (row.id === id) {
          doomed = row;
          return false;
        }
        return true;
      });
      if (doomed && doomed.url && /^blob:/i.test(doomed.url)) {
        try {
          URL.revokeObjectURL(doomed.url);
        } catch (e) {}
      }
      persist();
      paint();
    }

    function uploadFile(file) {
      if (!cfg.loggedIn) return;
      if (uploadBusy) return; // du1021: one upload at a time (no double change events)
      if (uploadCount() >= PHOTO_CAP) {
        showStatus(cfg.photoCap, true);
        return;
      }
      uploadBusy = true;
      setAdding(true);
      showStatus(cfg.photoPreparing, false, true);
      prepareUploadFile(file)
        .then(
          function (ready) {
            try {
              sendUpload(ready);
            } catch (e) {
              showStatus(cfg.photoUploadError, true);
              uploadBusy = false;
              setAdding(false);
            }
          },
          function (err) {
            showStatus(uploadErrorMessage(err, cfg), true);
            uploadBusy = false;
            setAdding(false);
            if (uploadInput) uploadInput.value = '';
          }
        );
    }

    function sendUpload(file) {
      var optimistic = addOptimisticUpload(file);
      showStatus(cfg.photoAdding, false, true);
      proxyUpload(cfg.proxy, file, { addedAt: optimistic.addedAt })
        .then(function (data) {
          var incomingDoc = docFromResponse(data, starters, cfg.proxy);
          var uploaded = photoFromResponse(data, cfg.proxy);
          if (incomingDoc) {
            dropPhoto(optimistic.id);
            doc = mergeDocs(incomingDoc, doc);
            persist();
            paint();
          } else if (uploaded) {
            uploaded.source = 'upload';
            replacePhoto(optimistic.id, uploaded);
          }
          showStatus(cfg.photoAdded, false);
        })
        .catch(function (err) {
          // du1021: a failed upload never says "Photo added" (old 404 soft-fail
          // left a phantom photo that vanished on reload); raw server text is
          // mapped to plain words.
          dropPhoto(optimistic.id);
          showStatus(uploadErrorMessage(err, cfg), true);
        })
        .then(function () {
          uploadBusy = false;
          setAdding(false);
          if (uploadInput) uploadInput.value = '';
        });
    }

    function pickPhoto() {
      if (!cfg.loggedIn) return;
      if (uploadCount() >= PHOTO_CAP) {
        showStatus(cfg.photoCap, true);
        return;
      }
      if (uploadInput && typeof uploadInput.click === 'function') uploadInput.click();
    }

    function visiblePhotos() {
      if (!photosEl) return [];
      var figures = photosEl.querySelectorAll('[data-kl-photo]');
      var out = [];
      for (var i = 0; i < figures.length; i++) {
        if (!figures[i].hidden) out.push(figures[i]);
      }
      return out;
    }

    function isPersonalFig(fig) {
      return !!(fig && isPersonalSource(fig.getAttribute('data-source')));
    }

    function personalFigures() {
      if (!photosEl) return [];
      var all = photosEl.querySelectorAll('[data-kl-photo]');
      var out = [];
      for (var i = 0; i < all.length; i++) {
        if (isPersonalFig(all[i]) && !all[i].hidden) out.push(all[i]);
      }
      return out;
    }

    function collectPersonalIds() {
      var figs = personalFigures();
      var ids = [];
      for (var i = 0; i < figs.length; i++) {
        var id = figs[i].getAttribute('data-photo-id');
        if (id) ids.push(id);
      }
      return ids;
    }

    function firstStarterFig() {
      if (!photosEl) return null;
      var all = photosEl.querySelectorAll('[data-kl-photo]');
      for (var i = 0; i < all.length; i++) {
        if (all[i].getAttribute('data-source') === 'starter') return all[i];
      }
      return null;
    }

    function clearDragClasses() {
      if (!photosEl) return;
      photosEl.classList.remove('is-reordering');
      var figs = photosEl.querySelectorAll('[data-kl-photo]');
      for (var i = 0; i < figs.length; i++) {
        figs[i].classList.remove('is-dragging', 'is-drop');
        figs[i].style.touchAction = '';
      }
    }

    function placeDragged(fig, x, y) {
      var over = document.elementFromPoint(x, y);
      var target = over && over.closest ? over.closest('[data-kl-photo]') : null;
      if (!target || !photosEl.contains(target) || target === fig) return;
      var drop = photosEl.querySelector('.is-drop');
      if (drop && drop !== target) drop.classList.remove('is-drop');
      if (!isPersonalFig(target)) {
        var starter = firstStarterFig();
        if (starter) photosEl.insertBefore(fig, starter);
        target.classList.add('is-drop');
        return;
      }
      target.classList.add('is-drop');
      var rect = target.getBoundingClientRect();
      var before = x < rect.left + rect.width / 2 || y < rect.top + rect.height / 3;
      if (before) photosEl.insertBefore(fig, target);
      else photosEl.insertBefore(fig, target.nextSibling);
    }

    function persistPhotoOrder() {
      var ids = collectPersonalIds();
      var prev = (dragState && dragState.prevIds) || personalIdsFrom(doc);
      if (sameIdList(prev, ids)) return;
      applyPersonalOrder(doc, ids);
      persist();
      paint();
      proxyPost(cfg.proxy, { action: 'reorder_photos', photos: ids })
        .then(function () {
          return proxyGet(cfg.proxy).catch(function () {
            return null;
          });
        })
        .then(function (data) {
          if (!data) return;
          var incoming = docFromResponse(data, starters, cfg.proxy);
          if (!incoming) return;
          var incomingIds = personalIdsFrom(incoming);
          doc = mergeDocs(incoming, doc);
          applyPersonalOrder(doc, incomingIds.length ? incomingIds : ids);
          persist();
          paint();
        })
        .catch(function (err) {
          var status = err && err.status;
          if (status === 404) return;
          applyPersonalOrder(doc, prev);
          persist();
          paint();
          showStatus(cfg.reorderError, true);
        });
    }

    function endDrag(didMove) {
      if (!dragState) return;
      var fig = dragState.fig;
      try {
        if (fig && dragState.pointerId != null && fig.releasePointerCapture) {
          fig.releasePointerCapture(dragState.pointerId);
        }
      } catch (e) {}
      clearDragClasses();
      if (didMove) {
        skipPhotoOpen = true;
        persistPhotoOrder();
      }
      dragState = null;
    }

    function bindPhotoReorder() {
      if (!photosEl || !cfg.loggedIn || photosEl.getAttribute('data-kl-reorder-bound') === '1') return;
      photosEl.setAttribute('data-kl-reorder-bound', '1');

      photosEl.addEventListener('pointerdown', function (event) {
        if (event.button != null && event.button !== 0) return;
        if (lightboxIsOpen()) return;
        var target = event.target;
        if (!target || !target.closest) return;
        if (target.closest('[data-kl-remove-photo], [data-kl-hide-starter]')) return;
        var fig = target.closest('[data-kl-photo]');
        if (!isPersonalFig(fig) || !photosEl.classList.contains('is-sortable')) return;
        var fromGrip = !!target.closest('[data-kl-photo-grip]');
        dragState = {
          fig: fig,
          pointerId: event.pointerId,
          startX: event.clientX,
          startY: event.clientY,
          moved: false,
          fromGrip: fromGrip,
          prevIds: collectPersonalIds()
        };
        if (fromGrip && fig.setPointerCapture) {
          try {
            fig.setPointerCapture(event.pointerId);
          } catch (e) {}
        }
      });

      photosEl.addEventListener('pointermove', function (event) {
        if (!dragState || dragState.pointerId !== event.pointerId) return;
        var dx = event.clientX - dragState.startX;
        var dy = event.clientY - dragState.startY;
        if (!dragState.moved) {
          if (!dragState.fromGrip && dx * dx + dy * dy < 64) return;
          dragState.moved = true;
          dragState.fig.classList.add('is-dragging');
          dragState.fig.style.touchAction = 'none';
          photosEl.classList.add('is-reordering');
          if (dragState.fig.setPointerCapture) {
            try {
              dragState.fig.setPointerCapture(event.pointerId);
            } catch (e) {}
          }
        }
        event.preventDefault();
        placeDragged(dragState.fig, event.clientX, event.clientY);
      }, { passive: false });

      photosEl.addEventListener('pointerup', function (event) {
        if (!dragState || dragState.pointerId !== event.pointerId) return;
        endDrag(dragState.moved);
      });

      photosEl.addEventListener('pointercancel', function (event) {
        if (!dragState || dragState.pointerId !== event.pointerId) return;
        var moved = dragState.moved;
        endDrag(false);
        if (moved) paint();
      });
    }

    function lightboxIsOpen() {
      return !!(lightbox && (lightbox.open || lightbox.hasAttribute('open')));
    }

    function paintLightboxNav(figs) {
      var many = figs && figs.length > 1;
      if (lightboxNavs) lightboxNavs.hidden = !many;
      if (lightboxPrev) {
        lightboxPrev.hidden = !many;
        lightboxPrev.disabled = !many;
      }
      if (lightboxNext) {
        lightboxNext.hidden = !many;
        lightboxNext.disabled = !many;
      }
    }

    function showLightboxAt(index, figs) {
      figs = figs || visiblePhotos();
      if (!figs.length || !lightboxImg) {
        closeLightbox();
        return;
      }
      var len = figs.length;
      var wrap = ((index % len) + len) % len;
      lightboxIndex = wrap;
      var fig = figs[wrap];
      var img = fig.querySelector('img');
      if (!img) return;
      lightboxImg.src = img.currentSrc || img.src;
      lightboxImg.alt = img.alt || '';
      if (lightboxCap) {
        var cap = fig.querySelector('figcaption');
        lightboxCap.textContent = cap ? cap.textContent : '';
      }
      paintLightboxNav(figs);
    }

    function stepLightbox(delta) {
      if (!lightboxIsOpen()) return;
      var figs = visiblePhotos();
      if (figs.length < 2) return;
      showLightboxAt(lightboxIndex + delta, figs);
    }

    function openLightbox(fig) {
      if (!fig || !lightbox || !lightboxImg) return;
      var figs = visiblePhotos();
      var idx = 0;
      for (var i = 0; i < figs.length; i++) {
        if (figs[i] === fig) {
          idx = i;
          break;
        }
      }
      showLightboxAt(idx, figs);
      dialogOpen(lightbox);
    }

    function closeLightbox() {
      dialogClose(lightbox);
      if (lightboxImg) lightboxImg.removeAttribute('src');
      paintLightboxNav([]);
    }

    root.addEventListener('click', function (event) {
      var target = event.target;
      if (!target || !target.closest) return;

      var confirmOk = target.closest('[data-kl-notes-confirm-ok]');
      if (confirmOk) {
        event.preventDefault();
        confirmDelete();
        return;
      }
      var confirmCancel = target.closest('[data-kl-notes-confirm-cancel]');
      if (confirmCancel) {
        event.preventDefault();
        cancelDelete();
        return;
      }

      var lightboxClose = target.closest('[data-kl-lightbox-close]');
      if (lightboxClose) {
        event.preventDefault();
        closeLightbox();
        return;
      }
      var lightboxPrevBtn = target.closest('[data-kl-lightbox-prev]');
      if (lightboxPrevBtn) {
        event.preventDefault();
        stepLightbox(-1);
        return;
      }
      var lightboxNextBtn = target.closest('[data-kl-lightbox-next]');
      if (lightboxNextBtn) {
        event.preventDefault();
        stepLightbox(1);
        return;
      }
      var lightboxFrame = target.closest('[data-kl-lightbox-frame]');
      if (
        lightboxFrame &&
        !target.closest('img') &&
        !target.closest('[data-kl-lightbox-navs], [data-kl-lightbox-prev], [data-kl-lightbox-next]')
      ) {
        if (swipeIgnoreClick) {
          swipeIgnoreClick = false;
          return;
        }
        event.preventDefault();
        closeLightbox();
        return;
      }

      var photoOpen = target.closest('[data-kl-photo-open]');
      if (photoOpen) {
        event.preventDefault();
        if (skipPhotoOpen) {
          skipPhotoOpen = false;
          return;
        }
        openLightbox(photoOpen.closest('[data-kl-photo]'));
        return;
      }
      if (target.closest('[data-kl-photo-grip]')) {
        event.preventDefault();
        return;
      }

      var saveBtn = target.closest('[data-kl-notes-save]');
      if (saveBtn) {
        event.preventDefault();
        upsertNote(true);
        return;
      }
      var notesToggle = target.closest('[data-kl-notes-toggle]');
      if (notesToggle) {
        event.preventDefault();
        var nextOpen = notesToggle.getAttribute('aria-expanded') !== 'true';
        writeNotesOpen(nextOpen);
        applyNotesCollapse(nextOpen);
        return;
      }
      var addMine = target.closest('[data-kl-notes-add]');
      if (addMine) {
        event.preventDefault();
        pickPhoto();
        return;
      }
      var newBtn = target.closest('[data-kl-notes-new]');
      if (newBtn) {
        event.preventDefault();
        lastListFocus = newBtn;
        openEditor(null);
        return;
      }
      var backBtn = target.closest('[data-kl-notes-back]');
      if (backBtn) {
        event.preventDefault();
        closeEditor(true);
        return;
      }
      var editorDel = target.closest('[data-kl-notes-editor-delete]');
      if (editorDel) {
        event.preventDefault();
        askDelete(root.getAttribute('data-editing-id') || '');
        return;
      }
      var openBtn = target.closest('[data-kl-note-open]');
      if (openBtn) {
        event.preventDefault();
        lastListFocus = openBtn;
        var openCard = openBtn.closest('[data-kl-note-id]');
        editNote(openCard && openCard.getAttribute('data-kl-note-id'));
        return;
      }
      var delBtn = target.closest('[data-kl-note-delete]');
      if (delBtn) {
        event.preventDefault();
        var delCard = delBtn.closest('[data-kl-note-id]');
        askDelete(delCard && delCard.getAttribute('data-kl-note-id'));
        return;
      }
      var hideBtn = target.closest('[data-kl-hide-starter]');
      if (hideBtn) {
        event.preventDefault();
        var fig = hideBtn.closest('[data-kl-photo]');
        toggleStarter(fig && fig.getAttribute('data-photo-id'));
        return;
      }
      var removeBtn = target.closest('[data-kl-remove-photo]');
      if (removeBtn) {
        event.preventDefault();
        var photoFig = removeBtn.closest('[data-kl-photo]');
        var photoId = photoFig && photoFig.getAttribute('data-photo-id');
        if (!photoId) return;
        askConfirm(cfg.photoRemoveConfirm, cfg.remove, function () {
          removePhoto(photoId);
        });
        return;
      }
      if (hiddenBtn && target.closest('[data-kl-notes-hidden-toggle]')) {
        event.preventDefault();
        showHidden = !showHidden;
        paint();
      }
    });

    if (lightbox) {
      lightbox.addEventListener('cancel', function (event) {
        event.preventDefault();
        closeLightbox();
      });
      lightbox.addEventListener('click', function (event) {
        if (event.target === lightbox) {
          if (swipeIgnoreClick) {
            swipeIgnoreClick = false;
            return;
          }
          event.preventDefault();
          closeLightbox();
        }
      });
      var swipeX = 0;
      var swipeY = 0;
      var swipeOn = false;
      lightbox.addEventListener(
        'touchstart',
        function (event) {
          var t = event.changedTouches && event.changedTouches[0];
          if (!t) return;
          if (event.target && event.target.closest && event.target.closest('[data-kl-lightbox-close]')) return;
          swipeOn = true;
          swipeX = t.clientX;
          swipeY = t.clientY;
        },
        { passive: true }
      );
      lightbox.addEventListener(
        'touchend',
        function (event) {
          if (!swipeOn) return;
          swipeOn = false;
          if (!lightboxIsOpen()) return;
          var t = event.changedTouches && event.changedTouches[0];
          if (!t) return;
          var dx = t.clientX - swipeX;
          var dy = t.clientY - swipeY;
          if (Math.abs(dx) < 48) return;
          if (Math.abs(dx) < Math.abs(dy) * 1.15) return;
          swipeIgnoreClick = true;
          stepLightbox(dx < 0 ? 1 : -1);
        },
        { passive: true }
      );
      document.addEventListener('keydown', function (event) {
        if (!lightboxIsOpen()) return;
        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          stepLightbox(-1);
        } else if (event.key === 'ArrowRight') {
          event.preventDefault();
          stepLightbox(1);
        }
      });
    }
    if (confirmEl) {
      confirmEl.addEventListener('cancel', function () {
        pendingDeleteId = '';
        pendingConfirmFn = null;
      });
    }

    bindPhotoReorder();
    persist();
    applyNotesCollapse(readNotesOpen());
    paint();

    if (uploadInput) {
      uploadInput.addEventListener('change', function () {
        var file = uploadInput.files && uploadInput.files[0];
        if (file) uploadFile(file);
      });
    }

    if (!cfg.loggedIn || !cfg.proxy) return;

    proxyGet(cfg.proxy)
      .then(function (data) {
        var incoming = docFromResponse(data, starters, cfg.proxy);
        if (incoming) {
          doc = mergeDocs(incoming, doc);
          persist();
          paint();
        }
      })
      .catch(function () {});
  }

  function ready(cb) {
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', cb);
    else cb();
  }

  document.addEventListener('keydown', function (event) {
    if (event.key !== 'Escape' && event.key !== 'Esc') return;
    var box = document.querySelector('[data-kl-notes-lightbox]');
    if (!box || !(box.open || box.hasAttribute('open'))) return;
    event.preventDefault();
    if (typeof box.close === 'function') {
      if (box.open) box.close();
    } else {
      box.removeAttribute('open');
      box.setAttribute('hidden', '');
    }
    var img = box.querySelector('[data-kl-lightbox-img]');
    if (img) img.removeAttribute('src');
  });

  ready(function () {
    var root = document.querySelector('[data-kl-notes-page]');
    if (root) bindRoot(root);
  });

  document.addEventListener('shopify:section:load', function (event) {
    var root = event.target && event.target.querySelector && event.target.querySelector('[data-kl-notes-page]');
    if (root) {
      root.removeAttribute('data-kl-bound');
      bindRoot(root);
    }
  });

  global.KLNotesPhotos = {
    metafield: { namespace: 'diary', key: 'notes_photos' },
    PHOTO_CAP: PHOTO_CAP,
    normalizeDoc: normalizeDoc,
    normalizePhoto: normalizePhoto,
    mergeDocs: mergeDocs,
    docFromResponse: docFromResponse,
    photoFromResponse: photoFromResponse,
    authImagePath: authImagePath,
    readNotesOpen: readNotesOpen,
    writeNotesOpen: writeNotesOpen,
    isPersonalSource: isPersonalSource,
    personalIdsFrom: personalIdsFrom,
    sameIdList: sameIdList,
    applyPersonalOrder: applyPersonalOrder,
    FILE_MAX: FILE_MAX,
    fileKind: fileKind,
    prepareUploadFile: prepareUploadFile,
    uploadErrorMessage: uploadErrorMessage,
    configFrom: configFrom
  };
})(typeof window !== 'undefined' ? window : this);
